import { useState } from 'react';

import { useCreateAppointment } from '../hooks/use-appointments';
import { useToastStore } from '../stores/toast-store';

import { CapabilityGate } from './CapabilityGate';

export interface AppointmentCreateModalProps {
  conversationId: string;
  open: boolean;
  onClose: () => void;
}

type AppointmentType =
  | 'technical_visit'
  | 'in_person_meeting'
  | 'video_call'
  | 'phone_call'
  | 'delivery'
  | 'other';

const TYPES: { value: AppointmentType; label: string }[] = [
  { value: 'phone_call', label: 'Ligação' },
  { value: 'video_call', label: 'Videochamada' },
  { value: 'in_person_meeting', label: 'Reunião presencial' },
  { value: 'technical_visit', label: 'Visita técnica' },
  { value: 'delivery', label: 'Entrega' },
  { value: 'other', label: 'Outro' },
];

/**
 * AppointmentCreateModal — agenda um appointment (ADR-115) na Conversa em Curso.
 *
 * Gated por capability `appointments` (Solo+). `source` fixo em `attendant`.
 * `scheduledAt` capturado via datetime-local e convertido para ISO no submit.
 */
export function AppointmentCreateModal({
  conversationId,
  open,
  onClose,
}: AppointmentCreateModalProps): JSX.Element | null {
  const create = useCreateAppointment();
  const [title, setTitle] = useState('');
  const [type, setType] = useState<AppointmentType>('phone_call');
  const [scheduledAt, setScheduledAt] = useState('');
  const [duration, setDuration] = useState('30');
  const [location, setLocation] = useState('');
  const [notes, setNotes] = useState('');

  if (!open) return null;

  const handleSubmit = (): void => {
    const durationMinutes = Number.parseInt(duration, 10);
    create.mutate(
      {
        conversationId,
        title,
        type,
        scheduledAt: new Date(scheduledAt).toISOString(),
        source: 'attendant',
        ...(Number.isFinite(durationMinutes) && durationMinutes >= 5 && { durationMinutes }),
        ...(location.length > 0 && { addressOrLocation: location }),
        ...(notes.length > 0 && { notes }),
      },
      {
        onSuccess: () => {
          onClose();
          setTitle('');
          setType('phone_call');
          setScheduledAt('');
          setDuration('30');
          setLocation('');
          setNotes('');
          useToastStore.getState().push({
            variant: 'success',
            title: 'Agendamento criado',
            description: 'Adicionado aos appointments da conversa.',
            durationMs: 3500,
          });
        },
        onError: (err) => {
          useToastStore.getState().push({
            variant: 'error',
            title: 'Falha ao agendar',
            description: err instanceof Error ? err.message : 'Erro desconhecido',
            durationMs: 6000,
          });
        },
      },
    );
  };

  const inputStyle = {
    background: 'var(--bg-surface-2)',
    borderColor: 'var(--border-default)',
  };

  return (
    <CapabilityGate capability="appointments">
      <div
        role="dialog"
        aria-label="Novo agendamento"
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      >
        <div
          className="w-full max-w-lg rounded-lg p-6 shadow-xl"
          style={{ background: 'var(--bg-surface)' }}
        >
          <h2 className="mb-4 text-lg font-semibold">Novo agendamento</h2>

          <label className="mb-3 block">
            <span className="mb-1 block text-sm font-medium">Título</span>
            <input
              className="w-full rounded border px-2 py-1"
              style={inputStyle}
              value={title}
              onChange={(e): void => setTitle(e.target.value)}
              disabled={create.isPending}
              maxLength={200}
              placeholder="Ex.: visita para troca do roteador"
            />
          </label>

          <label className="mb-3 block">
            <span className="mb-1 block text-sm font-medium">Tipo</span>
            <select
              className="w-full rounded border px-2 py-1"
              style={inputStyle}
              value={type}
              onChange={(e): void => setType(e.target.value as AppointmentType)}
              disabled={create.isPending}
            >
              {TYPES.map((t) => (
                <option key={t.value} value={t.value}>
                  {t.label}
                </option>
              ))}
            </select>
          </label>

          <label className="mb-3 block">
            <span className="mb-1 block text-sm font-medium">Data e hora</span>
            <input
              type="datetime-local"
              className="w-full rounded border px-2 py-1"
              style={inputStyle}
              value={scheduledAt}
              onChange={(e): void => setScheduledAt(e.target.value)}
              disabled={create.isPending}
            />
          </label>

          <label className="mb-3 block">
            <span className="mb-1 block text-sm font-medium">Duração (min)</span>
            <input
              type="number"
              min={5}
              max={1440}
              className="w-full rounded border px-2 py-1"
              style={inputStyle}
              value={duration}
              onChange={(e): void => setDuration(e.target.value)}
              disabled={create.isPending}
            />
          </label>

          <label className="mb-3 block">
            <span className="mb-1 block text-sm font-medium">Local (opcional)</span>
            <input
              className="w-full rounded border px-2 py-1"
              style={inputStyle}
              value={location}
              onChange={(e): void => setLocation(e.target.value)}
              disabled={create.isPending}
              maxLength={500}
            />
          </label>

          <label className="mb-3 block">
            <span className="mb-1 block text-sm font-medium">Observações (opcional)</span>
            <textarea
              className="w-full rounded border px-2 py-1"
              style={inputStyle}
              rows={2}
              value={notes}
              onChange={(e): void => setNotes(e.target.value)}
              disabled={create.isPending}
              maxLength={2000}
            />
          </label>

          <div className="mt-4 flex justify-end gap-2">
            <button
              type="button"
              className="rounded border px-4 py-2 text-sm"
              style={{ borderColor: 'var(--border-default)' }}
              onClick={onClose}
              disabled={create.isPending}
            >
              Cancelar
            </button>
            <button
              type="button"
              className="btn btn--primary"
              onClick={handleSubmit}
              disabled={create.isPending || title.trim().length === 0 || scheduledAt.length === 0}
            >
              {create.isPending ? 'Agendando…' : 'Agendar'}
            </button>
          </div>
        </div>
      </div>
    </CapabilityGate>
  );
}
